import { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://vwwjda2e99.us-west-2.awsapprunner.com';

const isLoginPath = () => {
  const p = window.location.pathname.replace(/\/$/, '') || '/';
  return p === '/login';
};

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  // Sender Email Authorization States
  const [senderEmailAuthorized, setSenderEmailAuthorized] = useState(true);
  const [senderEmailUserDetails, setSenderEmailUserDetails] = useState(null);
  const [senderEmailAuthLoading, setSenderEmailAuthLoading] = useState(false);

  const checkSenderEmailAuthorization = async () => {
    setSenderEmailAuthLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/check-sender-email-authorization`);

      if (response.data && response.data.authorized) {
        setSenderEmailAuthorized(true);
        setSenderEmailUserDetails(response.data.user || null);
      } else {
        setSenderEmailAuthorized(false);
        setSenderEmailUserDetails(null);
      }
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error('Error checking Sender Email authorization:', error);
      }
      setSenderEmailAuthorized(false);
      setSenderEmailUserDetails(null);
    } finally {
      setSenderEmailAuthLoading(false);
    }
  };

  const handleAuthorization = async (type = 'senderEmails') => {
    const width = 500;
    const height = 600;
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;

    const popup = window.open(
      `${API_BASE_URL}/drive/auth/google?state=senderEmails`,
      'Authorize Sender Email',
      `width=${width},height=${height},top=${top},left=${left}`
    );

    if (popup) {
      popup.focus();

      const messageListener = (event) => {
        if (event.origin !== API_BASE_URL) return;

        if (event.data && event.data.success) {
          setSenderEmailAuthorized(true);
          if (event.data.user) {
            setSenderEmailUserDetails(event.data.user);
          }
          setTimeout(() => {
            checkSenderEmailAuthorization();
          }, 500);
        } else if (event.data && !event.data.success) {
          console.error('Authorization failed:', event.data.message);
          setSenderEmailAuthorized(false);
        }

        window.removeEventListener('message', messageListener);
      };

      window.addEventListener('message', messageListener);
    } else {
      alert('Please allow popups for this website');
    }
  };

  const handleDisconnectAuthorization = async (type = 'senderEmails') => {
    setSenderEmailAuthLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/disconnect-sender-email-authorization`);

      if (response.data && response.data.disconnected) {
        setSenderEmailAuthorized(false);
        setSenderEmailUserDetails(null);
      }
    } catch (error) {
      console.error('Error disconnecting Sender Email authorization:', error);
      await checkSenderEmailAuthorization();
    } finally {
      setSenderEmailAuthLoading(false);
    }
  };

  useEffect(() => {
    if (isLoginPath()) {
      return;
    }
    checkSenderEmailAuthorization();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        // Sender Email Authorization
        senderEmailAuthorized,
        senderEmailUserDetails,
        senderEmailAuthLoading,
        checkSenderEmailAuthorization,
        // Shared Functions
        handleAuthorization,
        handleDisconnectAuthorization
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
